/**
 * „Ohne Code erfassen“ -- die Suche unter dem Scanner, im Einzel- wie im
 * Mehrfachscan (KONZEPT.md §20).
 */

import { useEffect, useMemo, useState } from "react";
import type { ArtikelMitZustand } from "@thw/contracts";
import { useBestand } from "../lokal/bestand.js";
import { suchen } from "../lokal/suche.js";
import { rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Knopf, Nummer, StatusPunkt } from "./bausteine.jsx";

/** Pause nach dem letzten Tastendruck, bevor gesucht wird. */
const SUCHPAUSE_MS = 180;

/** Mehr Vorschlaege liest an der Einsatzstelle niemand. */
const HOECHSTENS = 15;

/**
 * Ohne Code erfassen -- für Material ohne lesbares Etikett.
 *
 * Kabel tragen oft keines, und ein Etikett kann zerkratzt oder verschmutzt
 * sein (KONZEPT.md §20). Statt die vollständige Nummer zu verlangen, wird
 * **gesucht**: Die letzten Ziffern genügen („9281" findet 0141-029281), die
 * Bezeichnung tut es auch. Ein Tipp auf einen Treffer öffnet ihn wie ein
 * Scan. So im Inventur-Werkzeug erprobt.
 */
export function OhneCode({
  aufArtikel,
  aufNichtDabei,
}: {
  aufArtikel: (id: string) => void;
  aufNichtDabei: (eingabe: string) => void;
}) {
  const [eingabe, setEingabe] = useState("");
  const [gesucht, setGesucht] = useState("");
  const [aktiv, setAktiv] = useState(-1);

  // Erst suchen, wenn das Tippen kurz ruht -- sonst eine Anfrage je Taste.
  useEffect(() => {
    const uhr = setTimeout(() => {
      setGesucht(eingabe.trim());
      setAktiv(-1);
    }, SUCHPAUSE_MS);
    return () => {
      clearTimeout(uhr);
    };
  }, [eingabe]);

  // Aus der lokalen Kopie -- dieselbe Suche wie auf der Startseite, offline
  // genauso wie online.
  const bestand = useBestand();
  const data = useMemo(
    () =>
      bestand === undefined || gesucht.length < 2
        ? undefined
        : suchen(bestand.artikel.values(), bestand.zustaende, gesucht, { limit: HOECHSTENS }),
    [bestand, gesucht],
  );
  const ortName = (id: string | null): string | null =>
    id === null ? null : (bestand?.lagerorte.get(id)?.kennung ?? null);

  const aktuell = gesucht === eingabe.trim();
  const treffer = aktuell && gesucht.length >= 2 ? (data?.treffer ?? []) : [];

  const waehlen = (eintrag: ArtikelMitZustand): void => {
    rueckmeldungGeben(true);
    aufArtikel(eintrag.artikel.id);
  };

  return (
    <section className="flex flex-col gap-2">
      <label className="flex flex-col gap-1" htmlFor="ohne-code">
        <span className="text-sm font-semibold">Ohne Code erfassen</span>
        <span className="text-sm text-neutral-600 dark:text-neutral-300">
          Die letzten Ziffern der Inventarnummer genügen — oder die Bezeichnung.
        </span>
      </label>
      <input
        id="ohne-code"
        className="min-h-12 rounded-xl border-2 border-neutral-300 bg-white px-4 text-base dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
        value={eingabe}
        onChange={(e) => {
          setEingabe(e.target.value);
        }}
        placeholder="z. B. 9281 oder Leitungsroller"
        autoComplete="off"
        enterKeyHint="search"
        role="combobox"
        aria-expanded={treffer.length > 0}
        aria-controls="ohne-code-treffer"
        aria-activedescendant={aktiv >= 0 ? `ohne-code-${String(aktiv)}` : undefined}
        onKeyDown={(e) => {
          // Pfeiltasten und Enter, damit es am Rechner ohne Maus geht.
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            if (treffer.length === 0) return;
            e.preventDefault();
            const schritt = e.key === "ArrowDown" ? 1 : -1;
            setAktiv((alt) => (alt + schritt + treffer.length) % treffer.length);
            return;
          }
          if (e.key !== "Enter") return;
          e.preventDefault();

          // Ein hervorgehobener Treffer gewinnt; sonst nur, wenn es genau
          // einen gibt. Bei mehreren waere jede Wahl geraten -- und ein falsch
          // gebuchtes Geraet faellt niemandem auf.
          const hervorgehoben = aktiv >= 0 ? treffer[aktiv] : undefined;
          if (hervorgehoben !== undefined) waehlen(hervorgehoben);
          else if (treffer.length === 1 && treffer[0] !== undefined) waehlen(treffer[0]);
          else if (treffer.length === 0 && eingabe.trim() !== "") {
            // Noch keine Vorschlaege -- etwa, weil ein Handscanner schneller
            // war als die Suchpause. Dann wie einen Scan aufloesen.
            aufNichtDabei(eingabe.trim());
          }
        }}
      />

      {gesucht.length >= 2 && aktuell && (
        <div aria-live="polite">
          {data !== undefined && treffer.length === 0 && (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">Nichts gefunden.</p>
          )}
          {treffer.length > 0 && (
            <ul id="ohne-code-treffer" role="listbox" className="flex flex-col gap-1">
              {treffer.map((t, i) => {
                const ort = ortName(t.zustand.lagerort_id);
                return (
                  <li
                    key={t.artikel.id}
                    id={`ohne-code-${String(i)}`}
                    role="option"
                    aria-selected={i === aktiv}
                  >
                    <button
                      type="button"
                      className={`flex min-h-14 w-full flex-col justify-center gap-1 rounded-xl px-4 py-2 text-left active:bg-neutral-200 dark:active:bg-neutral-700 ${
                        i === aktiv
                          ? "bg-blue-100 ring-2 ring-blue-700 dark:bg-blue-950"
                          : "bg-white dark:bg-neutral-800"
                      }`}
                      onClick={() => {
                        waehlen(t);
                      }}
                    >
                      <span className="font-semibold">{t.artikel.bezeichnung}</span>
                      <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        <Nummer>{t.artikel.inventarnummer}</Nummer>
                        <StatusPunkt status={t.zustand.status} />
                        {t.artikel.einheit_zustaendig !== null && (
                          <span className="text-neutral-600 dark:text-neutral-300">
                            {t.artikel.einheit_zustaendig}
                          </span>
                        )}
                        {ort !== null && (
                          <span className="text-neutral-600 dark:text-neutral-300">{ort}</span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {eingabe.trim() !== "" && (
        <Knopf
          art="still"
          onClick={() => {
            aufNichtDabei(eingabe.trim());
          }}
        >
          Nicht dabei
        </Knopf>
      )}
    </section>
  );
}
